import {Component, ElementRef, EventEmitter, Injector, Input, Output, SimpleChanges, ViewChild, ViewEncapsulation} from '@angular/core';
import {GeneralChartComponent} from "../general-chart/general-chart.component";
import { HC_COLORS, HC_HEX } from '../highcharts-shared';
import {MatTableDataSource} from "@angular/material/table";
import {MatSort, MatSortable} from "@angular/material/sort";
import {PortfolioService} from "../../pages/portfolio/portfolio.service";
import {NavigationStateService} from "../../shared/services/navigation-state.service";
import { HighchartsChartComponent } from "highcharts-angular";
import { MaterialModule } from "src/app/app.materials";
import { InfoComponent } from "src/app/components/info/info.component";
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AreaChartComponent } from "../area-chart/area-chart.component";
import { DonutChartComponent } from "../donut-chart/donut-chart.component";
import * as Highcharts from "highcharts/highstock";
import { ExportService } from 'src/app/shared/services/export.service';
import { NextBestTradeComponent, TradePoint } from 'src/app/components/next-best-trade/next-best-trade.component';


@Component({
  selector: 'app-efficient-frontier-chart',
  templateUrl: './efficient-frontier-chart.component.html',
  styleUrls: ['./efficient-frontier-chart.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [HighchartsChartComponent, MaterialModule, InfoComponent, FormsModule, CommonModule, AreaChartComponent, DonutChartComponent, NextBestTradeComponent]
})
export class EfficientFrontierChartComponent extends GeneralChartComponent {
  @Input() optimWeights: any[] = [];
  @Input() isPrint = false;
  @Input() optim: string;
  @Input() optimizationData: any = null;
  @Input() efficientChartMaxSharpeData: any = null;
  @Input() resultsTableData: any[] = [];
  @Input() minVolatility: any;
  @Input() maxVolatility: any;
  @Input() volatilityLine: any;

  @Output() pfOptim: EventEmitter<any> = new EventEmitter<any>();
  @Output() calculateClicked = new EventEmitter<any>();
  @Output() sliderChanged = new EventEmitter<any>();
  @Output() optimizationDataChange = new EventEmitter<any>();
  @ViewChild(MatSort, {static: true}) sort: MatSort;

  @ViewChild('slider', { read: ElementRef }) slider: ElementRef;

  sliderLocked = true;
  viewSavedCustomTrade = false;

  showStocks = true;
  showBenchmark = false;
  showUnconstrainted = false;
  showEfWeights = false;
  service: PortfolioService;
  navigationStateService: NavigationStateService;
  dataSource = new MatTableDataSource<any>();
  columns: any[] = ['company_name', 'weight'];

  companyData: any[] = []; // store company return, volatility, and sharpe ratio indexed by ticker

  sliderVal = 0;
  sliderMax = 100;
  sliderData: any = [];
  sliderPortfolioData: any = {};
  sliderVolatility: any = 0;
  sliderLoaded = false;

  // Risk/return/sharpe of the most recently applied NBT step, used to render
  // the "Selected portfolio" marker + footer row when viewSavedCustomTrade is ON.
  savedCustomTradeMetrics: { x: number; y: number; sharpe: number } | null = null;

  // Fixed point properties
  fixedPointTangencyPoint: number | null = null;
  fixedMaxSharpePoint: number | null = null;

  customPointAdded = false;
  currentIndex = 0;
  currentReturn: any = {}

  areaChartData: any[] = [];
  areaChartColors: string[] = [];
  cardExporting: Record<string, boolean> = {};
  exportService: ExportService;
  constructor(injector: Injector) {
    super(injector);
    this.chartConstructor = 'chart';
    this.service = injector.get(PortfolioService);
    this.navigationStateService = injector.get(NavigationStateService);
    this.exportService = injector.get(ExportService);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.optimWeights) {
      this.optimWeights = changes.optimWeights.currentValue;
      this.initAreaChart();
    }
    if (changes.optim) {
      this.optim = changes.optim.currentValue;
      this.initSlider();
    }
    if (changes.optimizationData) {
      this.optimizationData = changes.optimizationData.currentValue;
      if (!this.optim) {
        this.optim = this.optimizationData.optim_parameters.optim_performance;
      }
      this.viewSavedCustomTrade =
        this.optimizationData?.optim_parameters?.optim_performance === 'next_best_trade';
      this.initSlider();
    }
    if (changes.series) {
      this.series = changes.series.currentValue;
      this.initChart();
    }
    if (changes.efficientChartMaxSharpeData) {
      this.initSlider();
    }
    if (changes.resultsTableData) {
      this.resultsTableData = changes.resultsTableData.currentValue;
      this.initCompanyData();
    }
  }

  initAreaChart() {
    this.areaChartData = [];
    this.areaChartColors = [];
    const baseColor = HC_HEX.accent;
        
    const zip = (a, b) => a.map((k, i) => [k, b[i]]);
    let i = 0;
    let chartData: any[] = [];
    this.optimWeights.map((d: any) => {
        this.areaChartColors.push(
            Highcharts.color(baseColor).brighten((0.5 / this.optimWeights.length) * i).get('rgb') as string
        );
        chartData.push({
            name: d.company_name,
            data: zip(d.vol, d.id_w)
        });
        i++;
    });
    this.areaChartData = chartData;
  }

  initCompanyData() {
    this.companyData = this.resultsTableData.reduce((acc, d) => {
      acc[d.Ticker] = {
        historicalReturn: d.historicalReturn,
        expectedReturn: d.expectedReturn,
        volatility: d.volatility,
        sharpeRatio: d.sharpe
      };
      return acc;
    }, {});
    // Populate sliderData immediately once companyData is ready, without
    // waiting for the chart's 1000ms deferred initSlider call.
    this.initSlider();
    this.initChart();
  }

  getConstraintForAsset(asset: string) {
    let result = null;
    this.optimizationData.group_constraints.forEach(function (constraint) {
      if (constraint.type.id === 'asset') {
        if (constraint.items.some(item => item.name === asset)) {
          result = {
            min: constraint.min / 100,
            max: constraint.max / 100
          }
        }
      }
    })

    return result;
  }

  onFixedPointClick(index: number) {
    if (this.sliderLocked) {
      return;
    }
    this.sliderVal = index;
    this.addPointToChart();
  }


  onCompanyClick(ticker: string) {
    this.service.openCompanyInfoDialog(ticker)
  }

  addPointToChart() {
    // When the Saved Custom Trade view is ON, the table and chart marker are
    // driven by the persisted NBT weights instead of the slider index.
    if (this.viewSavedCustomTrade) {
      this.buildSavedCustomTradeTable();
      this.removeSelectedPortfolioMarker();
      this.addSelectedPortfolioMarkerForSavedTrade();
      return;
    }

    const index = this.sliderVal;
    if (this.companyData && Object.keys(this.companyData).length && this.optimWeights && this.optimWeights.length) {

      // update table data
      this.sliderData = [];
      this.optimWeights.map(o => {
        const companyName = o.company_name;
        const constraint = this.getConstraintForAsset(companyName);
        this.sliderData.push({
          ticker: o.Ticker,
          companyName: companyName,
          minWeight: constraint ? constraint.min * 100 : 0,
          maxWeight: constraint ? constraint.max * 100 : 0,
          allocation: o.id_w[index],
          expectedReturn: this.companyData[o.Ticker].expectedReturn,
          historicalReturn: this.companyData[o.Ticker].historicalReturn,
          sharpeRatio: this.companyData[o.Ticker].sharpeRatio,
          volatility: this.companyData[o.Ticker].volatility
        })
      })

      // order the slider data by the weight descending
      this.sliderData.sort((a, b) => b.allocation - a.allocation);
    }

    // Populate portfolio summary from input series data so the footer row
    // is filled even before the Highcharts instance is ready.
    if (this.series?.[0]?.data?.[index]) {
      const sp = this.series[0].data[index];
      this.sliderPortfolioData = {
        x: sp.x,
        y: sp.y,
        sharpeRatio: sp.sharpeRatio ?? (sp.x !== 0 ? sp.y / sp.x : 0),
      };
      this.sliderVolatility = sp.x;
    }

    this.removeSelectedPortfolioMarker();

    // Update chart
    if (this.chartInstance && this.chartInstance.series && this.chartInstance.series[0] && this.chartInstance.series[0].data) {
      const dataPoint = this.chartInstance.series[0].data[index];
      if (dataPoint) {
        // emit the volatility so area chart can be updated
        this.sliderVolatility = dataPoint.x;
        this.sliderPortfolioData = dataPoint;

        this.chartInstance.series[3].addPoint({
          name: 'Selected portfolio',
          x: dataPoint.x,
          y: dataPoint.y,
          color: HC_COLORS.accent,
          custom: {
            sharpeRatio: dataPoint.y / dataPoint.x,
          }
        });
        this.customPointAdded = true;
      }
    }
  }

  private removeSelectedPortfolioMarker() {
    if (this.customPointAdded && this.chartInstance?.series?.[3]?.data?.length) {
      const seriesData = this.chartInstance.series[3].data;
      for (let i = seriesData.length - 1; i >= 0; i--) {
        if (seriesData[i]?.name === 'Selected portfolio') {
          seriesData[i].remove(false);
          this.customPointAdded = false;
          break;
        }
      }
    }
  }

  private addSelectedPortfolioMarkerForSavedTrade() {
    if (!this.chartInstance?.series?.[3] || !this.savedCustomTradeMetrics) {
      return;
    }
    const { x, y, sharpe } = this.savedCustomTradeMetrics;
    this.sliderVolatility = x;
    this.chartInstance.series[3].addPoint({
      name: 'Selected portfolio',
      x,
      y,
      color: HC_COLORS.accent,
      custom: {
        sharpeRatio: sharpe,
      }
    });
    this.customPointAdded = true;
  }

  // Build the saved-trade table rows from optimizationData.modified_optim_weights,
  // using assetList + companyData to fill the per-asset columns. The footer row
  // ("Portfolio") is driven by savedCustomTradeMetrics.
  buildSavedCustomTradeTable() {
    const modified: any[] = (this.optimizationData?.modified_optim_weights ?? [])
      .filter((w: any) => w?.source === 'next_best_trade');
    const weightByTicker: Record<string, number> = {};
    for (const w of modified) {
      weightByTicker[w.ticker] = w.weight;
    }

    const rows: any[] = [];
    if (this.optimWeights?.length && this.companyData && Object.keys(this.companyData).length) {
      this.optimWeights.forEach((o: any) => {
        const weight = weightByTicker[o.Ticker];
        if (weight === undefined) return;
        const companyName = o.company_name;
        const constraint = this.getConstraintForAsset(companyName);
        const c = this.companyData[o.Ticker] ?? {};
        rows.push({
          ticker: o.Ticker,
          companyName,
          minWeight: constraint ? constraint.min * 100 : 0,
          maxWeight: constraint ? constraint.max * 100 : 0,
          // id_w values are already percentages (0-100), match that format.
          allocation: +(weight * 100).toFixed(2),
          expectedReturn: c.expectedReturn,
          historicalReturn: c.historicalReturn,
          sharpeRatio: c.sharpeRatio,
          volatility: c.volatility
        });
      });
    }
    rows.sort((a, b) => b.allocation - a.allocation);
    this.sliderData = rows;

    if (this.savedCustomTradeMetrics) {
      this.sliderPortfolioData = {
        x: this.savedCustomTradeMetrics.x,
        y: this.savedCustomTradeMetrics.y,
        sharpeRatio: this.savedCustomTradeMetrics.sharpe,
      };
      this.sliderVolatility = this.savedCustomTradeMetrics.x;
    }
  }

  onCustomTradeSaved(evt: { step: any; optimizationData: any }) {
    this.optimizationData = evt.optimizationData;
    // Bubble the refreshed optimizationData up so the parent's copy (used by
    // the main table via initViewTables) stays in sync.
    this.optimizationDataChange.emit(evt.optimizationData);
    if (evt.step) {
      this.savedCustomTradeMetrics = {
        x: +(evt.step.risk * 100).toFixed(2),
        y: +(evt.step.exp_return * 100).toFixed(2),
        sharpe: evt.step.sharpe,
      };
    }
    this.service.notificationService?.open(
      'Custom trade saved. Flip the View toggle to switch to the saved portfolio.'
    );
  }

  onSliderChange(event: any) {
    this.sliderVal = event;
    this.addPointToChart();
  }

  getFixedPointPosition(): string {
    return this.getPositionForIndex(this.fixedPointTangencyPoint);
  }

  getFixedMaxSharpePointPosition(): string {
    return this.getPositionForIndex(this.fixedMaxSharpePoint);
  }

  private getPositionForIndex(index: number | null): string {
    if (index === null || this.sliderMax <= 0) {
      return '0%';
    }
    
    // Check if slider element is available
    if (this.slider && this.slider.nativeElement) {
      let sliderWidth = this.slider.nativeElement.offsetWidth;
      sliderWidth = sliderWidth - 2; // remove padding
      if (sliderWidth > 0) {
        // Find index position in the slider data
        const indexPosition = this.series[0].data.findIndex(d => d.return_index === index);
        if (indexPosition >= 0) {
          const indexRatio = indexPosition / this.sliderMax;
          return indexRatio * sliderWidth + 'px';
        }
      }
    }
  }

  initSlider() {
    this.sliderLoaded = false;

    if (this.series && this.series.length && this.series[0].data.length) {
      // Keep all the positions, so efficient frontier slider can select all the data and allocations
      /* 
      // remove duplicated sharpeRatio values from the series data
      let uniqueData = this.series[0].data.filter((d, index, self) =>
        index === self.findIndex((t) => t.sharpeRatio === d.sharpeRatio)
      );
      this.sliderMax = uniqueData.length - 1;
      this.series[0].data = uniqueData; */
      this.sliderMax = this.series[0].data.length - 1;
    }
    if (this.efficientChartMaxSharpeData && this.series && this.series.length && this.series[0].data.length) {
      let maxSharpeIndex = 0;
      let maxSharpeValue = 0;
      // find the index of the tangency point
      this.series[0].data.map((d, index) => {
        if (d.sharpeRatio > maxSharpeValue) {
          maxSharpeValue = d.sharpeRatio;
          maxSharpeIndex = index;
        }
        if (d.x === this.efficientChartMaxSharpeData.x && d.y === this.efficientChartMaxSharpeData.y) {
          this.fixedPointTangencyPoint = index
        }
      })
      this.fixedMaxSharpePoint = maxSharpeIndex;

      if (!this.optim || this.optim == 'efficient_portfolio_max_sharpe_ratio') {
        this.sliderVal = this.fixedPointTangencyPoint ?? 0;
      } else if (this.optim == 'efficient_portfolio_given_mu') {
        const returnVal = this.optimizationData.optim_parameters.efficient_target_return;
        this.sliderVal = this.findReturnIndex(returnVal);
      }
    }

    if (this.series && this.series.length && this.series[0].data.length) {
      this.addPointToChart();
    }
    this.sliderLoaded = true;
  }

  private findReturnIndex(returnVal) {
    let index = this.series[0].data.findIndex(d => d.y === returnVal);
    if (index > -1) {
      return index
    }
    // try to find the closest index if it is not in return list
    returnVal = returnVal - 0.01;
    index = this.series[0].data.findIndex(d => d.y === returnVal);
    if (index > -1) {
      return index
    }

    // still not found, try the other way
    returnVal = returnVal + 0.02;
    index = this.series[0].data.findIndex(d => d.y === returnVal);
    if (index > -1) {
      return index
    }
    console.log("Warning: Can not find efficient_target_return in chart data.")
    return 0;
  }

  private getThemeColor(variable: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  }

  initChart() {
    this.customPointAdded = false;

    const textColor   = this.getThemeColor('--text');
    const textDim     = this.getThemeColor('--text-dim');
    const borderColor = this.getThemeColor('--border-strong');
    const fontUi      = this.getThemeColor('--font-ui') || 'Inter, sans-serif';

    const setCurrentIndex = (idx, vol, ret) => {
      if (!this.customPointAdded) {
        this.currentIndex = idx;
        this.currentReturn = {
          vol: vol.toFixed(1),
          ret: ret.toFixed(1)
        }
        if(idx < 101){
          this.optimWeights = this.optimWeights.map(o => {
            o.weight = o.id_w[this.currentIndex].toFixed(1);
            return o;
          })
        }else{

        }

         this.dataSource.data = this.optimWeights.filter((o)=>o.weight > 0).sort((a, b) => {return parseFloat(b.weight) - parseFloat(a.weight)});

         this.series[0].data.map(d => {
            if (d.return_index === idx || idx == 101){
              d.weight = this.dataSource.data;
            }
            return d;
          });
         
      }
    }

    const handleLineClick = (point?) => {

      if (!this.customPointAdded) {
        if (point && point.series.name == 'Optim Efficient Frontier') {
          // Add custom point to the list
          this.chartInstance.series[2].addPoint({
            name: 'Clicked Point',
            x: point.x,
            y: point.y,
            color: HC_COLORS.pos,
            custom: {
              sharpeRatio: point.y / point.x,
            }
          })
          this.customPointAdded = true;
        }
      } else {
        // Remove last point
        this.chartInstance.series[2].data[this.chartInstance.series[2].data.length-1].remove()
        this.customPointAdded = false;
      }
    };

    const findDataBehindOptim = (point?) => {

      this.series[0].data.map(d => {
       // if (d.x === point.x && d.y === point.y) {
          setCurrentIndex(101, d.x, d.y);
      //  }
      });
    };

    // This Highcharts build does not auto-run the cross-series data label
    // collision pass, so `allowOverlap: false` alone leaves the clustered
    // asset/portfolio labels stacked on top of each other. Collect every
    // visible data label on each (re)draw and run the hide pass manually.
    // labelrank decides who wins a collision (higher = kept): named portfolio
    // and trade markers always outrank individual asset labels, and assets
    // fall back to their expected return so the upper frontier stays labelled.
    const PRIORITY_LABELS = new Set([
      'Baseline Portfolio', 'Selected portfolio', 'Clicked Point',
      'Max Sharpe', 'Target Risk', 'Target Return', 'Max Return',
      'Minimum Risk', '1-Trade', '2-Trade', '3-Trade',
    ]);
    const hideOverlappingDataLabels = function () {
      const chart: any = this;
      const labels: any[] = [];
      chart.series.forEach((s: any) => {
        if (s.options?.dataLabels?.enabled === false) {
          return;
        }
        s.points.forEach((p: any) => {
          if (!p.dataLabel) {
            return;
          }
          p.dataLabel.newOpacity = 1;
          p.dataLabel.labelrank = PRIORITY_LABELS.has(p.name) ? 1000 : (p.y ?? 0);
          labels.push(p.dataLabel);
        });
      });
      if (labels.length) {
        chart.hideOverlappingLabels(labels);
      }
    };

    this.chartOptions = {
      chart: {
        height: '630px',
        backgroundColor: 'transparent',
        plotBackgroundColor: 'transparent',
        plotBorderWidth: 0,
        style: { fontFamily: fontUi },
        events: {
          click: function() {
            handleLineClick();
          },
          render: hideOverlappingDataLabels,
        }
      },
      credits: {
        enabled: false
      },
      title: {
        text: 'Efficient Frontier',
        style: {
          textTransform: 'none',
          color: textColor,
          fontFamily: fontUi,
          fontWeight: '600',
          fontSize: '14px',
        }
      },
      xAxis: {
        title: {
          text: 'Expected Volatility',
          style: {
            textTransform: 'capitalize',
            color: textDim,
            fontFamily: fontUi,
          }
        },
        labels: {
          format: '{value}%',
          style: {
            color: textDim,
            fontFamily: fontUi,
            fontSize: '11px',
          }
        },
        gridLineColor: borderColor,
        lineColor: borderColor,
        tickColor: borderColor,
        minPadding: 0.2,
        maxPadding: 0.2,
      },
      yAxis: {
        minorGridLineWidth: 0,
        title: {
          text: 'Expected Return',
          style: {
            textTransform: 'capitalize',
            color: textDim,
            fontFamily: fontUi,
          }
        },
        labels: {
          format: '{value}%',
          style: {
            color: textDim,
            fontFamily: fontUi,
            fontSize: '11px',
          }
        },
        gridLineColor: borderColor,
      },
      legend: {
        enabled: false,
      },
      colors: [HC_COLORS.accent, HC_COLORS.accent2, HC_COLORS.pos],
      
      series: this.series,
      tooltip: {
        enabled: true,
        formatter: function () {
          let data: any = (this as any).point;
          var res = ''
          if(data.options.weight)
            for(let i = 0; i< data.options.weight.length; i ++){
                res += "<br /><p style='color=#00E5A0'>" + data.options.weight[i].company_name + ": " + data.options.weight[i].weight + "%</p>";
            }         
          const sharpeRatio = data.options.custom?.sharpeRatio ?? data.options.sharpeRatio;
          return (data.company || data.name || 'Efficient Frontier') + '<br/> Expected Volatility: ' + data.x + '%<br/>Expected Return: ' + data.y + '%' + (sharpeRatio !== undefined ? '<br/>Sharpe Ratio: ' + +sharpeRatio.toFixed(2) : '')  + res;
        }
      },
      plotOptions: {
        scatter: {
          dataLabels: {
            enabled: true,
              formatter: function() {
                  let name: any = (this as any).point;
                  var color: string = HC_COLORS.accent;
                  if(name.name == "Baseline Portfolio")
                    color = HC_COLORS.pos;
                  if(name.name == "Max Sharpe"
                  || name.name == 'Target Risk' 
                  || name.name == 'Max Return'
                  || name.name == 'Target Return'
                  || name.name == 'Minimum Risk'
                  ){
                    color = HC_COLORS.accent2;
                  } else if (name.name.includes("Benchmark")) {
                    color = HC_COLORS.warn;
                  } else if (name.name === '1-Trade' || name.name === '2-Trade' || name.name === '3-Trade') {
                    color = HC_COLORS.warn;
                  }

                  return '<span style="color: ' + color + '; font-size: 12px;">' + name.name + '</span>';
              },
            allowOverlap: false,
            padding: 4,
            position: 'right',
            style: {
              textOutline: 'none',
              textShadow: 'none',
              fontFamily: fontUi,
            },
          }
        },
        series: {
          point: {
            events: {
              mouseOver: function () {
                // Purple line hover, set weight index
                if (this.series.name == 'Optim Efficient Frontier') {
                  const point: any = this;
                  setCurrentIndex(point.return_index, point.x, point.y)
                }
                if (this.series.name == 'Optim') {
                  findDataBehindOptim(this);
                }
              },
              click: function(event) {
                handleLineClick(event.point)
              }
            }
          }
        }
      },
    };
    if (this.fullHeight) {
      this.chartOptions.chart.height = '88%';
    }
    setTimeout(() => {
      this.loaded.set(true);
    });
    setTimeout(() => {
      this.initSlider();
    }, 1000);
  }
  calculate(hasData = true) {
    // Disable risk analysis navigation for 10 seconds
    this.navigationStateService.disableRiskAnalysis();
    
    // Set timer to re-enable after 10 seconds
    setTimeout(() => {
      this.navigationStateService.enableRiskAnalysis();
    }, 10000);
    
    this.calculateClicked.emit({
      return: this.optimizationData.optim_parameters.efficient_target_return,
      risk: this.optimizationData.optim_parameters.efficient_target_risk,
      hasData: hasData
    })
  }

  onLockChange() {
    if (this.sliderLocked) {
      this.calculateWithSlider();
    }
  }

  calculateWithSlider() {
    if (!this.sliderLocked) {
      return;
    }
    const sliderData = this.chartInstance.series[0].data[this.sliderVal].options;
    if (sliderData) {
      this.optimizationData.optim_parameters.efficient_target_return = sliderData.y;
      this.optimizationData.optim_parameters.action = "risk-analysis";
      this.service.changePfOptim('efficient_portfolio_given_mu');
      this.calculate(false);
    }
  }

  get hasSavedCustomTrade(): boolean {
    const modified = this.optimizationData?.modified_optim_weights;
    if (!Array.isArray(modified) || !modified.length) return false;
    return modified.some((w: any) => w?.source === 'next_best_trade');
  }

  onCustomTradeViewChange() {
    if (!this.optimizationData?.optim_parameters) {
      return;
    }
    this.optimizationData.optim_parameters.action = 'risk-analysis';

    if (this.viewSavedCustomTrade) {
      // Switch the entire app (Risk Page, Charts, Main Table) to the saved
      // Next Best Trade scenario by re-running risk-analysis with that optim.
      this.service.changePfOptim('next_best_trade');
      this.calculate(false);
      // Repaint the efficient frontier card immediately so the user sees
      // the saved trade's table + marker without waiting on risk-analysis.
      this.buildSavedCustomTradeTable();
      this.removeSelectedPortfolioMarker();
      this.addSelectedPortfolioMarkerForSavedTrade();
      this.service.notificationService?.open(
        'Switching to Saved Custom Trade view, updated analysis will be ready shortly.'
      );
    } else {
      // Flip back to the optimal slider-driven portfolio view. Mirrors
      // calculateWithSlider but runs regardless of the slider lock state.
      const sliderOptions = this.chartInstance?.series?.[0]?.data?.[this.sliderVal]?.options;
      if (sliderOptions) {
        this.optimizationData.optim_parameters.efficient_target_return = sliderOptions.y;
      }
      this.service.changePfOptim('efficient_portfolio_given_mu');
      this.calculate(false);
      // Restore the slider-index-driven table + marker locally.
      this.addPointToChart();
      this.service.notificationService?.open(
        'Switching to Optimal Slider Portfolio view, updated analysis will be ready shortly.'
      );
    }
  }

  onPfChange(value: string){
    this.service.changePfOptim(value);
    this.calculate(false);
  }
  onStockToggleChange() {
    if (this.showStocks) {
      this.chartInstance.series[3].show();
    } else {
      this.chartInstance.series[3].hide();
    }
  }
  onBenchmarkToggleChange() {
    if (this.showBenchmark) {
      this.chartInstance.series[4].show();
    } else {
      this.chartInstance.series[4].hide();
    }
  }

  onEfWeightsToggleChange() {
    this.showEfWeights = !this.showEfWeights;
  }


  onOptimizeClick(){

    
  }
  onConstraintToggleChange() {
    if (this.showUnconstrainted) {
      this.chartInstance.series[2].show();
    } else {
      this.chartInstance.series[2].hide();
    }
  }

  get baselineMetrics(): { risk: number; expReturn: number; sharpe: number } | null {
    const optimScatterData = this.series?.[3]?.data as any[] | undefined;
    if (!optimScatterData?.length) return null;
    const pt = optimScatterData.find(d => d.name === 'Baseline Portfolio');
    if (!pt) return null;
    const sharpe = pt.sharpeRatio ?? (pt.x !== 0 ? pt.y / pt.x : 0);
    return { risk: pt.x, expReturn: pt.y, sharpe };
  }

  get selectedPortfolioMetrics(): { risk: number; expReturn: number; sharpe: number } | null {
    const d = this.sliderPortfolioData;
    if (!d?.x && !d?.y) return null;
    const sharpe = d.options?.custom?.sharpeRatio ?? d.sharpeRatio ?? (d.x !== 0 ? d.y / d.x : 0);
    return { risk: d.x, expReturn: d.y, sharpe };
  }

  onTradePointsChanged(points: TradePoint[]): void {
    if (!this.chartInstance?.series?.[3]) return;

    // Remove ALL existing NBT trade points regardless of position in the series
    const data = this.chartInstance.series[3].data;
    for (let i = data.length - 1; i >= 0; i--) {
      if (data[i]?.options?.custom?.isNbtPoint) {
        data[i].remove(false);
      }
    }

    for (const pt of points) {
      this.chartInstance.series[3].addPoint({
        name: pt.label,
        x: pt.x,
        y: pt.y,
        color: HC_COLORS.warn,
        marker: { symbol: 'diamond', radius: 6 },
        custom: {
          sharpeRatio: pt.sharpe,
          isNbtPoint: true,
        }
      }, false);
    }

    this.chartInstance.redraw();
  }

  formatLabel(value: number) {
    return '';
  }

  handleCardExport(key: string, element: HTMLElement | null, filename: string): void {
    if (!element) {
      this.service?.notificationService?.open('Card not found. Please try again.');
      return;
    }
    this.cardExporting[key] = true;
    const runExport = async () => {
      try {
        await this.exportService.exportElementAsImage(element, {
          filename
        });
      } finally {
        this.cardExporting[key] = false;
      }
    };
    setTimeout(runExport, 0);
  }
  exportResultsChart() {
    const element = document.querySelector('#efficient-frontier-chart-results') as HTMLElement;
    this.handleCardExport('results', element, `efficient-frontier-chart-results.jpeg`);
  }
  exportResultsTable() {
    const element = document.querySelector('#efficient-frontier-chart-results-table') as HTMLElement;
    this.handleCardExport('resultsTable', element, `efficient-frontier-chart-results-table.jpeg`);
  }
  exportTransitionMap() {
    const element = document.querySelector('#efficient-frontier-chart-transition-map') as HTMLElement;
    this.handleCardExport('transitionMap', element, `efficient-frontier-chart-transition-map.jpeg`);
  }
  exportSummaryWeightsDonut() {
    const element = document.querySelector('#efficient-frontier-chart-summary-weights-donut') as HTMLElement;
    this.handleCardExport('summaryWeightsDonut', element, `efficient-frontier-chart-summary-weights-donut.jpeg`);
  }
  exportSummaryReturnsDonut() {
    const element = document.querySelector('#efficient-frontier-chart-summary-returns-donut') as HTMLElement;
    this.handleCardExport('summaryReturnsDonut', element, `efficient-frontier-chart-summary-returns-donut.jpeg`);
  }
  

}
